<script setup>
import { XMarkIcon } from '@heroicons/vue/24/outline'

const now = new Date()
const config = useRuntimeConfig()
const route = useRoute()

const { data } = await useFetch(config.public.BANNER_JSON_URL, {
  query: { affiliate: 'certificates_react' },
  getCachedData (key) {
    return useNuxtData(key).data.value
  },
  timeout: 5000,
  onResponseError () {
    return { data: [] }
  }
})

const phase = data.value?.flat()
  .map((item) => ({
    ...item,
    dateString: item.ends,
    remaining: new Date(item.ends) - now,
    prelude: stylize(item.prelude),
    subtitle: stylize(item.subtitle),
    homePrelude: stylize(item.homePrelude),
    header: stylize(item.header),
    homeHeader: stylize(item.homeHeader),
    title: stylize(item.title),
    addendumBefore: stylize(item.addendumBefore)
  }))
  .find(item => item.remaining > 0)

function stylize (string) {
  return string?.replace('[', '<span class="text-[#61DAFB]">').replace(']', '</span>')
}

const isBannerClosed = ref(true)

const closeBanner = () => {
  isBannerClosed.value = true
  localStorage.setItem('banner-react', false)
}

onMounted(() => {
  const banner = localStorage?.getItem('banner-react')

  if (banner === null) {
    isBannerClosed.value = false
  } else {
    isBannerClosed.value = true
  }
})

const showBannerOnLanding = computed(() => {
  if (route?.path.includes('/react') && !route?.path?.includes('/blog')) {
    return true
  } else {
    return phase.showOnHomepage || false
  }
})

const currentTheme = computed(() => {
  return phase?.theme || 1
})

const allowBannerOnPage = inject('allowBanners', true)
</script>

<template>
  <div
    v-show="!isBannerClosed && phase && showBannerOnLanding && allowBannerOnPage"
    class="relative">
    <NuxtLink
      :to="phase?.link ? phase?.link : '/react#pricing'"
      :target="route?.path === '/' || phase?.external ? '_blank' : '_self'"
      class="dark group relative flex h-14 w-full justify-center bg-[#14424E]">
      <div
        v-if="currentTheme === 'bf'"
        class="flex items-center justify-center gap-2 md:w-auto md:gap-5 xl:gap-20">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-4">
          <img
            :src="
              phase?.icon
                ? `/images/banner-${phase?.icon}.svg`
                : '/images/banner-winter.svg'
            "
            class="hidden h-4 w-auto md:h-6"
            alt="logo">
          <div
            class=" hidden !text-center text-[12px] font-black  !leading-none text-white md:inline md:w-[120px]  md:text-base xl:w-auto [&>span]:text-react-blue"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-5 xl:gap-20">
          <div class="flex w-auto flex-col items-center gap-0.5 md:w-auto xl:flex-row xl:gap-1">
            <div
              v-if="phase?.prelude"
              class="text-center text-[11px] font-black leading-none text-white md:hidden [&>span]:text-react-blue"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.title"
              class="text-center text-[11px] font-normal !leading-none text-white md:whitespace-nowrap md:text-base xl:text-lg [&>b]:font-bold [&>span]:font-bold [&>span]:text-still-yellow"
              v-html="phase?.title" />
            <div
              v-if="phase?.subtitle"
              class="text-center text-[8px] font-normal !leading-none text-white md:text-sm xl:inline xl:text-base"
              v-html="phase?.subtitle" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-0 md:px-2 md:py-1 bg-transparent md:bg-react-blue/20" />
          </div>

          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'react-offer'"
        class="flex items-center justify-center gap-2 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-2">
          <div
            class="w-12 !text-center text-[10px] font-bold leading-none text-white md:block md:w-auto md:text-lg [&>span]:text-[#61DAFB]"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-10">
          <div class="flex  shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
            <div
              v-if="phase?.prelude"
              class="text-center text-xs font-semibold text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-[#61DAFB]"
              v-html="phase?.prelude" />
            <div class="flex flex-col items-center gap-2 md:flex-row">
              <NuxtImg
                src="/images/fire-react.svg"
                class="hidden h-auto w-6 lg:block"
                alt="ang-day-logo" />
              <div
                v-if="phase?.title"
                class="text-center text-xs font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] [&>span]:text-[#61DAFB]"
                v-html="phase?.title" />
              <div
                v-if="phase?.subtitle"
                class="block text-center text-[9px] font-normal !leading-none text-white md:text-base xl:inline xl:text-base [&>span]:text-[#61DAFB]"
                v-html="phase?.subtitle" />
            </div>
          </div>
          <div
            v-if="phase?.interlude"
            class="hidden text-center text-[10px] font-normal text-white md:block md:text-base xl:inline xl:text-base [&>span]:font-bold [&>span]:text-react-blue"
            v-html="phase?.interlude" />
          <div
            v-if="phase?.cta"
            class="hidden h-[30px] items-center justify-center gap-2 rounded-md bg-[#61DAFB] px-4 text-sm  font-bold text-black group-hover:bg-white group-hover:text-eerie-black">
            <span>{{ phase?.cta }}</span>
            <span><svg
              width="12"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                fill="currentColor" />
            </svg></span>
            <span />
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white !p-0 sm:!p-1 md:!px-2 md:!py-1 sm:bg-[#61DAFB]/20"
              box-style="" />
          </div>

          <div class="flex flex-col items-center gap-x-2 gap-y-0 md:flex-row">
            <p
              v-if="phase?.preAddendum"
              class="text-[10px] font-bold uppercase text-white">
              {{ phase?.preAddendum }}
            </p>
            <div
              v-if="phase?.addendum"
              class=" rounded-md text-center text-[10px] font-bold text-white md:w-auto md:bg-[#61DAFB]/20 md:p-2 md:px-4 md:py-1 md:text-base"
              style="letter-spacing: 1.2px">
              {{ phase?.addendum }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'react-launch-eb'"
        class="flex items-center justify-center gap-2 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-2">
          <NuxtImg
            src="/images/fire-react.svg"
            class="hidden h-auto w-6 lg:block"
            alt="ang-day-logo" />
          <div
            class="w-12 !text-center text-[10px] font-bold leading-none text-white md:block md:w-auto md:text-base [&>span]:text-[#61DAFB]"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-10">
          <div class="flex  shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
            <div
              v-if="phase?.prelude"
              class="text-center text-xs font-semibold text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-[#61DAFB]"
              v-html="phase?.prelude" />
            <div class="flex flex-col items-center gap-2 md:flex-row">
              <div
                v-if="phase?.title"
                class="text-center text-xs font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] [&>span]:text-[#61DAFB]"
                v-html="phase?.title" />
              <div
                v-if="phase?.subtitle"
                class="block text-center text-[9px] font-normal !leading-none text-white md:text-base xl:inline xl:text-base [&>span]:text-[#61DAFB]"
                v-html="phase?.subtitle" />
            </div>
          </div>
          <div
            v-if="phase?.interlude"
            class="w-20 text-center text-[8px] font-normal text-white md:block md:w-auto md:text-sm xl:inline xl:text-base [&>span]:font-bold [&>span]:text-react-blue"
            v-html="phase?.interlude" />
          <div
            v-if="phase?.cta"
            class="hidden h-[30px] items-center justify-center gap-2 rounded-md bg-[#61DAFB] px-4 text-sm font-bold  text-black group-hover:bg-white group-hover:text-eerie-black md:flex">
            <span>{{ phase?.cta }}</span>
            <span><svg
              width="12"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                fill="currentColor" />
            </svg></span>
            <span />
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap text-[8px] font-bold uppercase text-[#61DAFB] md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white !p-0 sm:!p-1 md:!px-2 md:!py-1 sm:bg-[#61DAFB]/20"
              box-style="" />
          </div>

          <div class="flex flex-col items-center gap-x-2 gap-y-0 md:flex-row">
            <p
              v-if="phase?.preAddendum"
              class="text-[10px] font-bold uppercase text-white">
              {{ phase?.preAddendum }}
            </p>
            <div
              v-if="phase?.addendum"
              class=" rounded-md text-center text-[10px] font-bold text-white md:w-auto md:bg-[#61DAFB]/20 md:p-2 md:px-4 md:py-1 md:text-base"
              style="letter-spacing: 1.2px">
              {{ phase?.addendum }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'autumn-sales'"
        class="my-auto w-full">
        <div class="mx-auto flex max-w-7xl -translate-x-0 items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
          <div class="flex items-center gap-2">
            <InlineSvg
              src="/images/autumn-leaf.svg"
              class="size-6 text-[#61DAFB] md:size-8"
              alt="Autumn Sales Banner" />
            <p
              class="w-18 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-[#61DAFB]"
              v-html="phase?.prelude" />
          </div>
          <div class="[&>p]:!leading-1 flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20">
            <p
              class="text-center text-sm font-normal text-white md:text-lg"
              v-html="phase?.title" />

            <p
              v-if="phase?.interlude"
              class="text-xs font-bold uppercase tracking-[1.8px] text-[#61DAFB] md:text-lg"
              v-html="phase?.interlude" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXTENDED')"
              class="whitespace-nowrap text-[8px]  font-bold uppercase text-[#61DAFB] md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-[#61DAFB] md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-[#61DAFB]/20"
              box-style="" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'halloween-sale'"
        class="my-auto w-full">
        <div class="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
          <div class="flex items-center gap-2">
            <InlineSvg
              src="/images/pumpkin.svg"
              class="hidden size-6 sm:block  md:size-6"
              alt="Halloween Sales Banner" />
            <p
              class="w-24 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-[#FF6666]"
              v-html="phase?.prelude" />
          </div>
          <div class="flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20 [&>p]:!leading-[90%]">
            <p
              class="text-center text-sm font-normal text-white md:text-lg"
              v-html="phase?.title" />

            <p
              v-if="phase?.interlude"
              class="text-xs font-bold uppercase tracking-[1.8px] text-react-blue md:text-lg"
              v-html="phase?.interlude" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="mt-1 flex flex-col items-center md:mt-0 md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXTENDED')"
              class="whitespace-nowrap text-[8px]  font-bold uppercase text-react-blue md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="-mb-1 whitespace-nowrap text-[8px] font-bold uppercase text-white sm:mb-0 md:text-[10px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-[#FF6666] md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-[#FF6666]/20"
              box-style="" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'conf'"
        class="flex items-center justify-center gap-2 md:gap-8 xl:gap-10">
        <div
          class="hidden items-center gap-2 md:flex md:gap-4">
          <InlineSvg
            src="/images/icon-fire.svg"
            class="h-4 w-auto text-[#61DAFB] md:h-5"
            alt="fire" />
          <div
            class="w-12 text-center text-xs !font-bold text-[#61DAFB] md:block md:w-auto md:text-sm xl:text-base"
            v-html="phase?.prelude" />
        </div>
        <div class="flex flex-row items-center gap-1 md:gap-3 lg:flex-row lg:gap-5">
          <div class="flex w-[200px] flex-col flex-wrap items-center justify-center gap-x-1 md:w-auto lg:flex-row lg:flex-nowrap lg:gap-8 [&>span]:text-[#61DAFB]">
            <div
              class="hidden text-[10px] font-bold text-[#61DAFB]"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.header"
              class=" items-center gap-4 text-center text-[10px] font-semibold  text-white md:w-auto md:whitespace-nowrap md:text-sm xl:text-base [&>span>span]:text-[#61DAFB]">
              <span v-html="phase.header" />
            </div>
            <div
              v-if="phase?.title"
              class="items-center gap-4 text-center text-[10px] font-semibold leading-none text-white md:w-auto md:whitespace-nowrap md:text-sm md:!leading-[1.2rem] xl:text-base [&>span>span]:text-[#61DAFB]">
              <span v-html="phase.title" />
            </div>
            <NuxtImg
              src="/images/banner-alain.png"
              class="ml-5 hidden h-[36px] rounded-full" />
            <div
              v-if="phase?.subtitle"
              class="text-center text-[10px] font-medium text-white md:text-xs xl:text-base min-[1600px]:block [&>b]:font-bold [&>b]:text-[#61DAFB] [&>span]:!font-semibold [&>span]:text-[#61DAFB]"
              v-html="phase?.subtitle" />
            <div
              v-if="phase?.interlude"
              class="block text-center text-[10px] font-bold text-white md:text-base md:leading-none lg:hidden xl:text-base [&>span]:font-bold [&>span]:text-[#61DAFB]"
              v-html="phase?.interlude" />
          </div>
          <div
            v-if="phase?.interlude"
            class="hidden text-center text-[10px] font-bold text-white md:text-sm lg:block xl:text-base [&>span]:font-bold [&>span]:text-[#61DAFB] lg:[&>span]:ml-2"
            v-html="phase?.interlude" />
          <div class="flex flex-col items-center gap-0 md:flex-row md:gap-2 lg:gap-0">
            <p
              v-if="phase?.preAddendum"
              class="rounded-md text-center text-[8px] font-bold uppercase text-white md:text-[10px] lg:bg-[#61DAFB]/20 lg:px-4 lg:py-1">
              {{ phase?.preAddendum }}
            </p>
            <div
              v-if="phase?.addendum"
              class="w-12 text-center text-[8px] font-bold text-white lg:w-auto  lg:px-4 lg:py-1 lg:text-xs lg:[&>span]:ml-2"
              style=""
              v-html="phase?.addendum" />
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-4">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap text-[8px] font-bold uppercase text-[#61DAFB] md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-[9px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
              class="dark"
              box-class="text-white lg:scale-[1.1] lg:translate-x-2 md:px-2 md:py-1 md:bg-[#61DAFB]/30" />
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'free-weekend'"
        class="flex -translate-x-3 items-center gap-3 md:w-auto md:translate-x-0 md:gap-10 xl:gap-10 2xl:gap-16 ">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-4">
          <img
            src="/images/banner-winter.svg"
            class="hidden h-4 w-auto md:h-5"
            alt="logo">
          <div
            class=" w-12 !text-left text-[10px] font-black leading-none  md:block md:w-auto md:text-lg lg:text-2xl [&>span]:text-react-blue"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-10 2xl:gap-14">
          <div class="flex shrink-0 flex-row flex-wrap items-center justify-center gap-x-3 gap-y-0 md:w-auto md:max-w-fit md:shrink md:flex-nowrap md:gap-x-10 lg:flex-row">
            <div
              v-if="phase?.prelude"
              class="!text-center text-[10px] md:hidden [&>span]:text-react-blue"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.title"
              class="w-[72px] text-left text-[14px] font-black uppercase leading-none text-white md:w-[95px] md:text-lg md:!leading-[1.2rem] lg:w-auto lg:whitespace-nowrap xl:leading-[1.2rem] [&>span]:text-react-blue"
              v-html="phase?.title" />
            <div
              v-if="phase?.subtitle"
              class="block w-[143px] text-center text-[10px] font-bold leading-tight text-white md:w-[247px] md:text-lg lg:w-auto xl:inline [&>em]:bg-gradient-to-r [&>em]:from-[#FF1A7A]  [&>em]:to-[#FFB800] [&>em]:bg-clip-text [&>em]:text-transparent [&>span]:text-react-blue"
              v-html="phase?.subtitle" />
            <div
              v-if="phase?.addendum"
              class="block text-center text-[10px] font-medium text-white md:text-sm lg:hidden lg:text-base xl:text-base [&>span]:font-bold [&>span]:text-react-blue"
              v-html="phase?.addendum" />
          </div>
          <div
            v-if="phase?.addendum"
            class="hidden text-center text-[10px] font-normal text-white md:text-base lg:block xl:inline xl:text-base [&>span]:font-bold [&>span]:text-react-blue"
            v-html="phase?.addendum" />

          <div
            v-show="phase?.cta"
            class="hidden h-[30px] items-center justify-center gap-2 whitespace-nowrap rounded-md bg-react-blue px-4 text-xs font-bold text-dark-gray  transition-all ease-linear group-hover:bg-react-blue/80 md:inline-flex">
            <span>{{ phase.cta }}</span>
            <span><svg
              width="14"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                fill="currentColor" />
            </svg></span>
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap bg-gradient-to-r from-react-blue to-dodger-blue bg-clip-text text-[8px]  font-bold uppercase text-transparent md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
              {{ phase?.countdownFooter || 'ends in' }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-react-blue/20"
              box-style="" />
          </div>
        </div>
      </div>
    </NuxtLink>
    <button
      class="absolute right-2 top-4 z-20 p-1 hover:bg-white/20 xl:right-3"
      @click="closeBanner">
      <span class="sr-only">close</span><XMarkIcon class="x-5 h-5 text-white" />
    </button>
  </div>
</template>

<style scoped>
.banner-text :deep(span) {
  @apply  text-nuxt-green
}
</style>
